import React, { useState, useEffect } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton
} from '@mui/material';
import {
  Warning as WarningIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { detectClinicalIntakeChanges, acknowledgeClinicalIntakeChanges } from '../services/clinicalIntake.service';
import RegenerationDialog from './RegenerationDialog';

const ClinicalIntakeChangeIndicator = ({ projectId, onRegenerateRequest, allSections = [], onRegenerationComplete }) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [regenerationDialogOpen, setRegenerationDialogOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    if (!projectId || dismissed) return;

    const checkChanges = async () => {
      try {
        setLoading(true);
        const result = await detectClinicalIntakeChanges(projectId);
        setHasChanges(result.hasChanges);
        setChanges(result.changes || []);
      } catch (error) {
        console.error('Error detecting changes:', error);
        setHasChanges(false);
      } finally {
        setLoading(false);
      }
    };

    // Check immediately (single-shot)
    checkChanges();

    // Re-check when the tab/window regains focus (avoids constant polling + server log spam)
    const onFocus = () => checkChanges();
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkChanges();
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [projectId, dismissed, refreshTrigger]);

  const handleOpenDialog = () => {
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleDismiss = () => {
    setDismissed(true);
    setDialogOpen(false);
  };

  const handleRegenerate = async (type, sectionTitles) => {
    setRegenerationDialogOpen(false);
    setDialogOpen(false);
    if (onRegenerateRequest) {
      setIsRegenerating(true);
      try {
        await onRegenerateRequest(type, sectionTitles, changes);
        // After regeneration completes, trigger a refresh to check if changes are gone
        setRefreshTrigger(prev => prev + 1);
        // Also call the parent callback if provided
        if (onRegenerationComplete) {
          onRegenerationComplete();
        }
      } finally {
        setIsRegenerating(false);
      }
    }
  };

  const handleViewChanges = () => {
    setDialogOpen(true);
  };

  // Mark changes as reviewed without regenerating (update lockedVersion snapshot)
  const handleMarkReviewed = async () => {
    if (!projectId) return;
    try {
      setLoading(true);
      await acknowledgeClinicalIntakeChanges(projectId);
      // After acknowledging, treat as no changes
      setHasChanges(false);
      setDismissed(true);
      setDialogOpen(false);
    } catch (error) {
      console.error('Error marking changes as reviewed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateClick = () => {
    setDialogOpen(false);
    setRegenerationDialogOpen(true);
  };

  if (!hasChanges || dismissed || loading) {
    return null;
  }

  // Group changes by section
  const changesBySection = changes.reduce((acc, change) => {
    const section = change.field.split('.')[0];
    if (!acc[section]) {
      acc[section] = [];
    }
    acc[section].push(change);
    return acc;
  }, {});

  return (
    <>
      <Alert
        severity="warning"
        icon={<WarningIcon />}
        action={
          <>
        <IconButton
          aria-label="refresh"
          color="inherit"
          size="small"
          onClick={() => {
            // manual refresh check (still cheap thanks to 304s, but user-controlled)
            setDismissed(false);
            // Trigger re-run by toggling dialogOpen briefly isn't needed; call API directly:
            (async () => {
              try {
                setLoading(true);
                const result = await detectClinicalIntakeChanges(projectId);
                setHasChanges(result.hasChanges);
                setChanges(result.changes || []);
              } finally {
                setLoading(false);
              }
            })();
          }}
          sx={{ mr: 0.5 }}
        >
          <RefreshIcon fontSize="inherit" />
        </IconButton>
        <Button
          color="inherit"
          size="small"
          onClick={handleViewChanges}
          sx={{ mr: 1 }}
        >
          View Changes
        </Button>
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleDismiss}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </>
        }
        sx={{ mb: 2 }}
      >
        <AlertTitle>Clinical Intake Form Has Been Modified</AlertTitle>
        The clinical intake form has been edited since the protocol was generated. 
        You may need to regenerate affected sections.
      </Alert>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Clinical Intake Form Changes
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The following fields have been modified in the clinical intake form:
          </Typography>

          {Object.entries(changesBySection).map(([section, sectionChanges]) => (
            <Box key={section} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {section.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Typography>
              <List dense>
                {sectionChanges.map((change, idx) => (
                  <ListItem key={idx}>
                    <ListItemText
                      primary={change.field.split('.').slice(1).join('.').replace(/_/g, ' ')}
                      secondary={
                        <Box>
                          <Chip
                            label={change.type}
                            size="small"
                            color={change.type === 'modified' ? 'warning' : change.type === 'added' ? 'success' : 'error'}
                            sx={{ mr: 1, mb: 0.5 }}
                          />
                          {change.oldValue && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              Old: {typeof change.oldValue === 'string' ? change.oldValue.substring(0, 100) : JSON.stringify(change.oldValue).substring(0, 100)}
                            </Typography>
                          )}
                          {change.newValue && (
                            <Typography variant="caption" color="text.secondary" component="div">
                              New: {typeof change.newValue === 'string' ? change.newValue.substring(0, 100) : JSON.stringify(change.newValue).substring(0, 100)}
                            </Typography>
                          )}
                        </Box>
                      }
                      // Ensure secondary content is wrapped in a <div>, not a <p>, to avoid invalid <div> inside <p>
                      secondaryTypographyProps={{ component: 'div' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          ))}

          <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Regenerating sections will update the protocol content based on the modified clinical intake form data.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleMarkReviewed} color="inherit">
            Mark Changes as Reviewed
          </Button>
          <Button
            onClick={handleRegenerateClick}
            variant="contained"
            color="primary"
            startIcon={<RefreshIcon />}
          >
            Regenerate Sections
          </Button>
          </DialogActions>
        </Dialog>

        {/* Regeneration Dialog */}
        <RegenerationDialog
          open={regenerationDialogOpen}
          onClose={() => setRegenerationDialogOpen(false)}
          changes={changes}
          allSections={allSections}
          onRegenerate={handleRegenerate}
          isRegenerating={isRegenerating}
        />
      </>
    );
  };

export default ClinicalIntakeChangeIndicator;

